import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input, type Signal, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { RestrictedElementDirective } from '@auth';
import { sortUtils } from '@core';
import { type ChildClient, type ClientUser, RoleNames, UserRolesIds, UserStatus } from '@models';
import { Store } from '@ngrx/store';
import { AuthSelectors, ClientManagementSelectors, ClientUsersActions, ClientUsersSelectors, type UserState } from '@ngrx-store';
import { CdkVirtualScrollViewportWrapperComponent, ConfirmationDialogComponent, NoDataPlaceholderComponent, SearchInputComponent, SkeletonTableComponent, VirtualTableScrollNormalizerDirective } from '@ui';
import { filter } from 'rxjs';

import { UserStatusComponent } from '../user-status/user-status.component';
import { FormatPhoneNumberPipe } from './format-phone-number.pipe';

type UserStatusFilter = 'current' | 'archived';

@Component({
  imports: [
    MatButtonToggleGroup,
    MatButtonToggle,
    RestrictedElementDirective,
    RouterLink,
    MatIcon,
    SearchInputComponent,
    MatSort,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatHeaderCellDef,
    NgClass,
    SkeletonTableComponent,
    NoDataPlaceholderComponent,
    MatHeaderRow,
    MatRow,
    MatTooltip,
    DatePipe,
    UserStatusComponent,
    MatSortHeader,
    MatRowDef,
    MatHeaderRowDef,
    CdkFixedSizeVirtualScroll,
    CdkVirtualScrollViewport,
    VirtualTableScrollNormalizerDirective,
    FormatPhoneNumberPipe,
    CdkVirtualScrollViewportWrapperComponent,
  ],
  selector: 'avs-users-table',
  styleUrl: './users-table.component.scss',
  templateUrl: './users-table.component.html',
})
export class UsersTableComponent {
  fillAvailableHeight = input(false);
  tableId = input<string | null>(null);
  isMobile = input(false);
  isInjected = input(false);

  protected currentClient: Signal<ChildClient | undefined>;
  protected readonly eUserRolesIds = UserRolesIds;
  protected readonly eRoleNames = RoleNames;
  protected readonly ClientUserStatus = UserStatus;
  protected displayedColumns: Signal<string[]>;
  protected dataSource = new MatTableDataSource<ClientUser>([]);
  protected userRole: Signal<UserRolesIds | undefined>;
  protected editor: Signal<UserState | undefined>;
  protected userListLoaded: Signal<boolean | undefined>;
  protected userStatusFilter = signal<UserStatusFilter>('current');
  protected search = signal('');

  private destroyRef = inject(DestroyRef);
  private sort = viewChild(MatSort);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private dialog = inject(MatDialog);

  constructor() {
    this.userRole = toSignal(this.store.select(AuthSelectors.userRole));
    this.userListLoaded = toSignal(this.store.select(ClientUsersSelectors.userListIsLoaded));
    this.editor = toSignal(this.store.select(AuthSelectors.selectAuthState));
    this.currentClient = toSignal(this.store.select(ClientManagementSelectors.getCurrentClient));

    this.displayedColumns = computed(() => {
      const columns = ['index', 'firstName', 'status', 'role', 'email', 'phoneNumber', 'lastLogin'];

      if (this.currentClient()?.requireTotp) {
        columns.splice(3, 0, 'totpStatus');
      }

      columns.push('archive');

      return columns;
    });

    this.dataSource.filterPredicate = this.filterLogic;

    this.store.select(ClientUsersSelectors.getCompanyUserList)
      .pipe(takeUntilDestroyed())
      .subscribe(users => {
        this.dataSource.data = users;
        this.applySortConfig();
        this.filterTable(this.search(), this.userStatusFilter());
      });

    effect(() => {
      const sortEl = this.sort();

      if (sortEl) {
        this.dataSource.sort = sortEl;
        this.cdr.detectChanges();
      }
    });

    effect(() => {
      this.filterTable(this.search(), this.userStatusFilter());
    });
  }

  protected archiveUser(user: ClientUser) {
    this.openConfirmationDialog({
      data: { isArchived: true },
      description: `Do you really want to archive ${user.firstName} ${user.lastName} ?`,
      title: 'Archive',
      userId: user.id,
    });
  }

  protected restoreUser(user: ClientUser) {
    this.openConfirmationDialog({
      data: { isArchived: false },
      description: `Do you really want to restore ${user.firstName} ${user.lastName} ?`,
      title: 'Restore',
      userId: user.id,
    });
  }

  protected resendInvitation(user: ClientUser) {
    this.store.dispatch(ClientUsersActions.resendUserInvitation({ clientUser: user }));
  }

  protected resetTotp(user: ClientUser) {
    ConfirmationDialogComponent.open(this.dialog, {
      description: `Do you really want to reset 2FA for ${user.firstName} ${user.lastName} ?`,
      title: 'Reset 2FA',
    })
      .pipe(
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.store.dispatch(ClientUsersActions.resetTotp({ clientUser: user })));
  }

  private applySortConfig() {
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'role') return item.role?.name || '';

      return (item[property as keyof ClientUser] as string) || '';
    };

    this.dataSource.sortData = sortUtils.naturalSorting;
  }

  private openConfirmationDialog({
    userId,
    title,
    description,
    data,
  }: {
    data: Partial<ClientUser>;
    description: string;
    title: string;
    userId: string;
  }) {
    ConfirmationDialogComponent.open(this.dialog, {
      description,
      title,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmation => {
        if (confirmation) {
          this.store.dispatch(ClientUsersActions.updateUserClient({ clientUser: data, clientUserId: userId }));
        }
      });
  }

  private filterLogic(record: ClientUser, filter: string) {
    const filterData = JSON.parse(filter);
    const statusMatch =
      filterData.userStatusFilter === 'archived'
        ? record.status === UserStatus.ARCHIVED
        : record.status !== UserStatus.ARCHIVED;
    const nameFilter =
      !filterData.search ||
      (record.firstName + ' ' + record.lastName + ' ' + record.email)
        .toLowerCase()
        .includes(filterData.search.toLowerCase());

    return statusMatch && nameFilter;
  }

  private filterTable(search: string, userStatusFilter: UserStatusFilter) {
    this.dataSource.filter = JSON.stringify({
      search,
      userStatusFilter,
    });
  }
}
